import React from 'react';

import { DataGridPro, DataGridProProps, GridCellEditStopParams, gridClasses, GridColDef, GridColumnResizeParams, GridEventListener, GridRenderCellParams, GridRenderEditCellParams, useGridApiRef } from '@mui/x-data-grid-pro';

import { AdapterSlice, AppModels, hasErrors, saveModel } from 'cdx-common';
import localStorage from '../localStorage';


import { Box, TextField } from '@mui/material';
import { appStore } from '../../store';

export type GridConfig = {
    [index: string]: GridColDef;
};

export const SimGrid = <T extends BaseAttrs,>(props: DataGridProProps<T> & {
    gridId: string,
    rows: T[],
    modelName: AppModels;
    slice: AdapterSlice;
    onRowUpdated?: (updated: T, original: T) => void;
}) => {

    const apiRef = useGridApiRef();

    const [config, setConfig] = localStorage.useLocalStorage('GridConfig' + props.gridId, {});

    const simColumns = React.useMemo(() => {
        return props.columns.map(p => baseColDef(config, p));
    }, [props.columns]);

    const saveConfig = async (newConfig: GridColDef) => {
        setConfig(newConfig);
    };

    const updateRow = async (updateRow: T, originalRow: T) => {
        const response = await saveModel<T>(props.modelName, updateRow);
        if (hasErrors(response)) {

        } else {
            appStore.dispatch(props.slice.actions.upsertOne(response));
            apiRef.current.updateRows([response]);
            if (props.onRowUpdated) {
                props.onRowUpdated(response, originalRow);
            }
        }
    };


    return <div ><DataGridPro
        density='compact'
        sx={{
            [`& .${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]: {
                outline: 'none',
            },
            [`& .${gridClasses.columnHeader}:focus, & .${gridClasses.columnHeader}:focus-within`]:
            {
                outline: 'none',
            },
        }}
        {...props}
        apiRef={apiRef}
        columns={simColumns}
        processRowUpdate={(update: T, original: T) => {
            void updateRow(update, original);
            return update;
        }}
        onColumnWidthChange={async (params: GridColumnResizeParams) => {
            if (config) {
                let currConfig = { ...config };
                currConfig[params.colDef.field] = currConfig?.[params.colDef.field] ?? {};
                currConfig[params.colDef.field].width = params.width;
                void saveConfig(currConfig);
            }
        }} /></div >;

};


export const baseColDef = (config: any, p: GridColDef): GridColDef => {

    return {
        ...p,
        width: config?.[p.field]?.width ?? p.width ?? null,
    };
};


export const EditableTextGridCell = (props: GridRenderCellParams) => {
    return <span className='editabletext-content' onClick={() => {
        props.api.startCellEditMode({
            field: props.field,
            id: props.id
        });
    }}>{props.value}</span>;
};

export const EditingTextGridCell = (props: GridRenderEditCellParams) => {

    const [value, setValue] = React.useState(props.value ?? '');
    const { id, field } = props;

    const onCellEditStop: GridEventListener<"cellEditStop"> = (params: GridCellEditStopParams) => {
        if (params.id == props.id && params.field == props.field) {
            props.api?.setEditCellValue({ id, field, value });
        }
    };

    const cleanCallback = props.api?.subscribeEvent('cellEditStop', onCellEditStop);

    React.useEffect(() => {
        return () => {
            cleanCallback();
        };
    }, []);

    React.useEffect(() => {
        setValue(props.value);
    }, [props.value]);


    return <Box sx={{ width: '100%', height: '100%' }}>
        <TextField
            value={value}
            defaultValue={props.value}
            onChange={(event) => {
                setValue(event.target.value);
            }}
            sx={{ margin: '0.5 0', padding: '.25em 0.5em', borderWidth: 1, borderColor: '#000', borderStyle: 'solid', borderRadius: 1 }} />
    </Box>;
};
